import React from 'react';
import EmptyState from './EmptyState';
import { reportUiCrash } from '../../utils/clientErrorReporter';
import './ErrorBoundary.css';

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </svg>
);

// public/index.html 의 asset-guard 와 같은 키·규칙을 쓴다: 세션당 1회, 60초 내 재발이면 자동 새로고침하지 않는다.
const ASSET_RELOAD_KEY = 'wq_asset_reload_at';
const ASSET_RELOAD_COOLDOWN_MS = 60 * 1000;

export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  return name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(message);
};

// 배포 뒤 열어둔 탭에서 lazy 라우트 청크(/chat 등)를 못 받았을 때 한 번은 조용히 새로고침으로 복구한다.
export const tryAutoReloadForChunkError = (error, { storage, now, reload } = {}) => {
  if (!isChunkLoadError(error)) return false;
  const store = storage || (typeof window !== 'undefined' ? window.sessionStorage : null);
  const at = typeof now === 'function' ? now() : Date.now();
  try {
    const last = Number(store?.getItem(ASSET_RELOAD_KEY) || 0);
    if (at - last < ASSET_RELOAD_COOLDOWN_MS) return false;
    store?.setItem(ASSET_RELOAD_KEY, String(at));
  } catch (e) {
    return false; // 저장소를 못 쓰면 무한 새로고침을 피해 안내 화면으로
  }
  (reload || (() => window.location.reload()))();
  return true;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(error, info);
    // 서버 로그로 보낸 뒤 새로고침한다(keepalive라 새로고침에도 전송은 이어진다).
    reportUiCrash(error, info);
    tryAutoReloadForChunkError(error);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="whatquiz-error-boundary">
          <EmptyState
            icon={<ErrorIcon />}
            title="문제가 생겼어요"
            description="잠시 후 다시 시도해주세요"
            primaryAction={{ label: '새로고침', onClick: this.handleReload }}
            secondaryAction={{ label: '홈으로 가기', onClick: this.handleGoHome }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
